import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  useTheme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import moment from "moment";

import Table, { ColumnProps } from "components/basic/table";
import Typography from "components/basic/typography";
import { EVENT_TYPE_MAP } from "../../../calendar/eventTypes";
import StatusChip from "../StatusChip";
import { CUSTOMER_RESPONSE_META, Meeting } from "../../types";

interface MeetingsTableProps {
  meetings: Meeting[];
  onView: (m: Meeting) => void;
  onConfirm: (m: Meeting) => void;
  onCancel: (m: Meeting) => void;
  onDelete: (m: Meeting) => void;
}

const RowActions: React.FC<{
  meeting: Meeting;
  onView: (m: Meeting) => void;
  onConfirm: (m: Meeting) => void;
  onCancel: (m: Meeting) => void;
  onDelete: (m: Meeting) => void;
}> = ({ meeting, onView, onConfirm, onCancel, onDelete }) => {
  const theme = useTheme();
  const { t } = useTranslation("meetings");
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const close = () => setAnchor(null);

  const canConfirm = meeting.displayStatus === "pending";
  const canCancel =
    meeting.displayStatus !== "cancelled" &&
    meeting.displayStatus !== "completed";

  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={close}>
        <MenuItem
          onClick={() => {
            close();
            onView(meeting);
          }}
        >
          <VisibilityOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
          {t("table.view")}
        </MenuItem>
        {canConfirm && (
          <MenuItem
            onClick={() => {
              close();
              onConfirm(meeting);
            }}
          >
            <CheckCircleOutlineIcon
              fontSize="small"
              style={{ marginRight: 8 }}
            />
            {t("table.confirm")}
          </MenuItem>
        )}
        {canCancel && (
          <MenuItem
            onClick={() => {
              close();
              onCancel(meeting);
            }}
          >
            <CancelOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
            {t("table.cancel")}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            close();
            onDelete(meeting);
          }}
          sx={{ color: theme.palette.error.main }}
        >
          <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
          {t("table.delete")}
        </MenuItem>
      </Menu>
    </>
  );
};

const MeetingsTable: React.FC<MeetingsTableProps> = ({
  meetings,
  onView,
  onConfirm,
  onCancel,
  onDelete,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("meetings");

  const columns: ColumnProps<Meeting>[] = [
    {
      header: t("table.customer"),
      align: "left",
      card: "primary",
      cell: (m) => {
        // Decline / reschedule already show in the Status column, so only the
        // "Accepted" response is surfaced here to avoid duplication.
        const response =
          m.customerResponse === "confirmed"
            ? CUSTOMER_RESPONSE_META.confirmed
            : undefined;
        return (
          <Box>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {m.customerName}
            </Typography>
            {m.customerPhone && (
              <Typography
                variant="caption"
                style={{
                  color: theme.palette.text.secondary,
                  display: "block",
                }}
              >
                {m.customerPhone}
              </Typography>
            )}
            {response && (
              <Typography
                variant="caption"
                style={{ color: response.color, fontWeight: 600 }}
              >
                {response.label}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      header: t("table.type"),
      align: "left",
      card: "inline",
      cell: (m) => {
        const meta = EVENT_TYPE_MAP[m.type];
        const Icon = meta.icon;
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Icon sx={{ fontSize: 18, color: meta.color }} />
            <Typography variant="body2">{meta.label}</Typography>
          </Stack>
        );
      },
    },
    {
      header: t("table.date_time"),
      align: "left",
      cell: (m) => (
        <Box>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {moment(m.date, "YYYY-MM-DD").format("ddd, MMM D, YYYY")}
          </Typography>
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary }}
          >
            {m.allDay
              ? t("table.all_day")
              : `${moment(m.from, "HH:mm").format("h:mm A")} – ${moment(
                  m.to,
                  "HH:mm",
                ).format("h:mm A")}`}
          </Typography>
        </Box>
      ),
    },
    {
      header: t("table.status"),
      align: "left",
      card: "inline",
      cell: (m) => <StatusChip status={m.displayStatus} />,
    },
    {
      header: t("table.job"),
      align: "left",
      cell: (m) => (
        <Box>
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {m.estimate?.estimateNumber
              ? `Job #${m.estimate.estimateNumber}`
              : m.title}
          </Typography>
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary }}
          >
            {m.estimate?.chargerModel ?? (m.estimate ? "" : "—")}
          </Typography>
        </Box>
      ),
    },
    {
      header: t("table.actions"),
      align: "center",
      card: "actions",
      cell: (m) => (
        <RowActions
          meeting={m}
          onView={onView}
          onConfirm={onConfirm}
          onCancel={onCancel}
          onDelete={onDelete}
        />
      ),
    },
  ];

  return <Table columns={columns} data={meetings} />;
};

export default MeetingsTable;
